import { PAGE_BREAKPOINTS_EXTENDED } from "@styles/common-styles";
import { ConvertUtils } from "@stellar/utils";
import { PALETTE_VARS } from "@styles/palette-vars";

/** A bare hex colour: three, four, six or eight hex digits with no leading "#". */
const BARE_HEX_COLOR = /^[0-9a-f]{3,4}$|^[0-9a-f]{6}$|^[0-9a-f]{8}$/i;

/** Defines the size in pixels for the header bar */
export const HEADER_HEIGHT = "3.75rem";

/** The width of the sidebar when in minified condition */
export const SIDEBAR_WIDTH_MINIFIED = 65;

/** The width of the sidebar when in open condition */
export const SIDEBAR_WIDTH_EXPANDED = 210;

/**
 * Defines the padding between the tab selector and the content.
 */
export const TABS_CONTENT_PADDING = "12px";

/**
 * CSS class to only show an element when the mouse is hovering over it.
 */
export const SHOW_ON_HOVER_CLASS = "show-on-hover";

/**
 * CSS class used to hide an element by default and show it only when its parent
 * (or a specific ancestor) is being hovered.
 *
 * This class should be used in combination with CSS that targets the hover state
 * of a container to conditionally reveal the element.
 */
export const HIDE_ON_HOVER_CLASS = "hide-on-hover";
const THOUSAND = 1000;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const GIGA_BYTE_SIZE = 1024 ** 3;

/** Default CSS style to apply when the button is hovered. */
export const hoverStyle = {
  backgroundColor: PALETTE_VARS.action.hover,
  ...getSvgColoredIconCss(PALETTE_VARS.primary.main)
};

/** Default CSS style to apply when the button is clicked. */
export const clickStyle = {
  backgroundColor: PALETTE_VARS.action.selected,
  ...getSvgColoredIconCss(PALETTE_VARS.primary.main)
};

/**
 * Adds a prefix hash "#" to a string if there's no one.
 * This is particularly useful for HEX colors
 *
 * Anything that is not a bare hex colour is returned untouched, so a palette value such as
 * `var(--mui-palette-text-secondary)` or `rgba(...)` survives instead of becoming `#var(...)`.
 *
 * @param str "The value to add the hash. E.g. FFFFFF"
 * @returns The value including the prefixed hash, e.g. "#FFFFFF".
 */
export function addPrefixHashIfMissing(str) {
  const hex = str ?? "";
  return BARE_HEX_COLOR.test(hex) ? `#${hex}` : hex;
}

/**
 * Exports the CSS properties to color an SVG icon, It colors all possible svg tags,
 * except the ones that have fill="none" because those ones are not colored and
 * instead are used as content wrappers.
 *
 * @param color CSS color to be used.
 */
export function getSvgColoredIconCss(color) {
  const hexColor = addPrefixHashIfMissing(color ?? "");

  /* eslint-disable @typescript-eslint/naming-convention -- The namings are CSS selectors */
  return {
    "path:not([fill='none'])": {
      fill: hexColor
    },
    "rect:not([fill='none'])": {
      fill: hexColor
    },
    "circle:not([fill='none'])": {
      fill: hexColor
    },
    "ellipse:not([fill='none'])": {
      fill: hexColor
    },
    "line:not([fill='none'])": {
      fill: hexColor
    },
    "polyline:not([fill='none'])": {
      fill: hexColor
    },
    "polygon:not([fill='none'])": {
      fill: hexColor
    }
  };
  /* eslint-enable */
}

/** Copies some text into to the clipboard */
export function copyToClipboard(text) {
  if (navigator && navigator.clipboard) {
    // If the native function is available, use it.
    void navigator.clipboard.writeText(text);
    return;
  }

  // Otherwise this is a workaround for browsers that don't support the native function.
  const copyArea = document.createElement("textarea");
  copyArea.value = text;
  // Needed so that it works in all browsers that generally support document.execCommand('copy').
  document.body.appendChild(copyArea);
  copyArea.select();

  // Should never throw an exception according to https://developer.mozilla.org/de/docs/Web/API/Document/execCommand
  document.execCommand("copy");
  document.body.removeChild(copyArea);
}

/**
 * Returns a CSS box-shadow for a bottom border.
 *
 * @param thickness The thickness of the border.
 * @param color The color of the border.
 * @returns The CSS box-shadow.
 */
export function getBottomBorderWithBoxShadow({
  thickness = "1px",
  color = PALETTE_VARS.primary.main
}) {
  return `0 ${thickness} 0 0 ${color}`;
}

/**
 * Exports the border CSS property.
 *
 * @param color Color of the border.
 * @param width Optional width of the border. Defaults to "1px".
 * @returns CSS property to be used in border.
 */
export function border(color, width = "1px") {
  return `${width} solid ${color}`;
}

/** Width of the cards depending on the breakpoint */
export const cardWidth = {
  xs: "100%",
  sm: "250px"
};

/** Exports properties to be used in cards to show projects, groups, etc. */
export const cardProperties = {
  height: "311px",
  marginTop: "20px",
  boxShadow: `0 0 0 1px ${PALETTE_VARS.divider}`,
  borderRadius: "4px",
  creationDatePaddingTop: "14px"
};

/**
 * Navigates in the app instead of following a link, unless a modifier key or the middle button
 * asks the browser to open the link in a new tab.
 *
 * @param event The click on the link
 * @param navigate Navigates to the link's target in the app
 */
export function navigateOnPlainClick(event, navigate) {
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
    return;
  }
  event.preventDefault();
  navigate();
}

/**
 * Returns the breakpoint name based on the width of the screen.
 *
 * @param width The width of the screen.
 * @returns The name of the breakpoint.
 */
export function getBreakpointName(width) {
  for (let i = 0; i < Object.values(PAGE_BREAKPOINTS_EXTENDED).length; i++) {
    if (width < Object.values(PAGE_BREAKPOINTS_EXTENDED)[i]) {
      return Object.keys(PAGE_BREAKPOINTS_EXTENDED)[i - 1];
    }
  }
  return "xxxl";
}

/**
 * Checks if the storage size is less than 1 TB, which means it should be displayed in GB
 * If max value is smaller than 1 TB, then it return true, otherwise false
 */
function shouldShowGBStorage(byteSize) {
  return byteSize < THOUSAND * GIGA_BYTE_SIZE;
}

/** Formats the storage size in a human-readable format, converting bytes to GB or TB as appropriate */
export function formatStorageSize(byteSize) {
  if (byteSize < 0) {
    return 0;
  }
  if (shouldShowGBStorage(byteSize)) {
    return ConvertUtils.bytesToGb(byteSize);
  }
  return ConvertUtils.bytesToTb(byteSize);
}

/** Returns the unit based on the maximum value. If max value is undefined or small than 1 TB, then it return GB, otherwise TB */
export function getStorageUnit(maxBytes) {
  if (maxBytes === undefined || maxBytes < 0) {
    return "GB";
  }
  if (shouldShowGBStorage(maxBytes)) {
    return "GB";
  }
  return "TB";
}

/**
 * Gets whether the browser supports hover interactions.
 * @returns True if the browser supports hover, false otherwise or the support cannot be determined.
 */
export function getIsHoverSupported() {
  try {
    return window.matchMedia("(hover: hover)").matches;
  } catch {
    // If the browser or test suit does not support `matchMedia` method then set as if hover is not supported
    return false;
  }
}